import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ICliente, IPopulate, IQueryParam } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import * as Highcharts from 'highcharts';
import HC_more from 'highcharts/modules/treemap';
import { Router, ActivatedRoute } from '@angular/router';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { IRegExpSearch } from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../../../../auxiliares/tabla/tabla.component';
import { LoginService } from '../../../../../login/login.service';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  CambiarPadreDialogComponent,
  TipoOperacionCliente,
} from '../cambiar-padre-dialog/cambiar-padre-dialog.component';
import { ClienteSelectorCacheService } from '../../../../../../componentes/selectores/cliente-selector/cliente-selector-cache.service';
HC_more(Highcharts);
@Component({
  selector: 'app-clientes-grafico',
  templateUrl: './clientes.component.html',
  styleUrl: './clientes.component.scss',
  standalone: false,
})
export class ListadoClientesGraficoComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);
  private clienteSelectorCacheService = inject(ClienteSelectorCacheService);

  private idClientePropio = LoginService.getCliente()?._id;
  //// Tabla nueva
  public name = 'ListadoClientesGraficoComponent';
  public nombreCliente: string = '';
  public idCliente: string = '';
  public clientes: ICliente[] = [];
  public totalCount = 0;
  public columnas?: IColumnas<ICliente>[];
  public search: IRegExpSearch = {
    fields: ['nombre'],
  };

  public populate: IPopulate = {};
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    select: 'nombre idPadre tipoCliente config',
    page: 0,
    includeChildren: true,
    childrenLevel: 0,
    unlimited: true, // Sin limite para obtener todos los clientes
  };
  right: any;

  public loading: boolean = true;
  public chartOptions?: Highcharts.Options;

  public async listar(): Promise<void> {
    const clientes = await firstValueFrom(
      this.clienteSelectorCacheService.getClientes(),
    );
    this.clientes = clientes.filter((c) => c._id !== this.idClientePropio);
    await this.graficoHijos();
  }

  // Acciones
  public async crear() {
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar() {
    this.navigator.navigate(['editar', this.idCliente], {
      relativeTo: this.route,
    });
  }
  public async notas() {
    this.navigator.navigate(['detalles', this.idCliente], {
      relativeTo: this.route,
    });
  }

  public async cambiarPadre() {
    await this.abrirDialog('cambiarPadre');
  }

  public async moverEntidades() {
    await this.abrirDialog('moverEntidades');
  }

  public async eliminarConDialog() {
    await this.abrirDialog('eliminarConMover');
  }

  private async abrirDialog(tipoOperacion: TipoOperacionCliente) {
    if (!this.idCliente || !this.nombreCliente) {
      this.helper.notifError('Tenés que seleccionar un cliente primero');
      return;
    }

    const dialogRef = this.formularios.abrir(CambiarPadreDialogComponent, {
      tamano: 'mediano',
      data: {
        idCliente: this.idCliente,
        nombreCliente: this.nombreCliente,
        tipoOperacion: tipoOperacion,
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      // Recargar los clientes para reflejar los cambios
      await this.listar();
    }
  }

  public async graficoHijos() {
    (window as any).myComponentInstance = this;
    const data = this.clientes;
    if (!data || data.length === 0) {
      return;
    }

    const accentColor = LoginService.getCliente().config?.tema?.accentColor;
    const primaryColor = LoginService.getCliente().config?.tema?.primaryColor;

    // IDs de clientes no-Finales que están en el dataset (son candidatos a ser padres en el gráfico)
    const idsNodosIndividuales = new Set(
      data.filter((c) => c.tipoCliente !== 'Final').map((c) => c._id as string),
    );

    // Agrupar clientes por su idPadre
    const hijosPorPadre = new Map<string, ICliente[]>();
    for (const cliente of data) {
      const padreId = cliente.idPadre || '';
      if (!hijosPorPadre.has(padreId)) {
        hijosPorPadre.set(padreId, []);
      }
      hijosPorPadre.get(padreId)!.push(cliente);
    }

    const points: any[] = [];
    let finalesRaizCount = 0; // acumula Finales cuyos padres no tienen nodo visible

    for (const [padreId, hijos] of hijosPorPadre.entries()) {
      // El padre en el gráfico solo es válido si es un nodo individual (no-Final) presente en el dataset.
      // Si es el cliente propio (raíz) o no existe en el dataset, los hijos quedan en el nivel raíz.
      const chartParent =
        padreId && idsNodosIndividuales.has(padreId) ? padreId : undefined;

      // Nodos individuales: Mayoristas y Minoristas
      for (const cliente of hijos) {
        if (cliente.tipoCliente !== 'Final') {
          points.push({
            id: cliente._id,
            name: cliente.nombre,
            parent: chartParent,
            color: accentColor + '44',
            value: 1,
          });
        }
      }

      const cantidadFinales = hijos.filter(
        (c) => c.tipoCliente === 'Final',
      ).length;

      if (cantidadFinales > 0) {
        if (chartParent) {
          // Nodo agrupado de Finales bajo un padre visible
          points.push({
            id: `finales-de-${padreId}`,
            name: `Clientes Finales (${cantidadFinales})`,
            parent: chartParent,
            color: accentColor + '88',
            value: 1,
          });
        } else {
          // Acumular en el único nodo raíz de Finales
          finalesRaizCount += cantidadFinales;
        }
      }
    }

    // Nodo raíz único para todos los Finales sin padre visible
    if (finalesRaizCount > 0) {
      points.push({
        id: 'finales-de-raiz',
        name: `Clientes Finales (${finalesRaizCount})`,
        color: accentColor + '88',
        value: 1,
      });
    }

    this.chartOptions = {
      chart: {
        margin: 0,
        height: 600,
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        pointFormat: '<span style="font-size:13px">{point.name}</span>',
        headerFormat: '',
      },

      series: [
        {
          type: 'treemap',
          name: 'Hijos',
          allowDrillToNode: true,
          animationLimit: 30,
          layoutAlgorithm: 'squarified',
          colorByPoint: false,
          dataLabels: {
            enabled: false,
          },
          borderColor: primaryColor,
          borderWidth: 1,
          borderHeight: 1,
          borderRadius: 5,
          states: {
            hover: {
              borderColor: 'white',
            },
          },
          levels: [
            {
              level: 1,
              layoutAlgorithm: 'squarified',
              groupPadding: 3,
              borderWidth: 5,
              borderHeight: 5,
              dataLabels: {
                enabled: true,
                style: {
                  fontSize: '20px',
                  padding: 15,
                },
              },
              levelIsConstant: false,
            } as any,
          ],
          accessibility: {
            exposeAsGroupOnly: true,
          },
          data: points,
        },
      ] as any,
      plotOptions: {
        treemap: {
          point: {
            events: {
              click: function () {
                const component = (window as any).myComponentInstance;
                component.hiceClick(this);
              },
            },
          },
        },
      },
      subtitle: {
        text: '',
        align: 'left',
      },
      title: {
        text: '',
        align: 'left',
      },
    };
  }

  public hiceClick(point: Highcharts.Point) {
    if ((point as any).id?.startsWith('finales-de-')) {
      // Nodo agregado de Finales: no representa un cliente individual
      this.nombreCliente = '';
      this.idCliente = '';
      return;
    }
    this.nombreCliente = point.name;
    this.idCliente = (point as any).id;
  }

  // Hooks

  async ngOnInit() {
    this.loading = true;
    await this.listar();
    this.loading = false;
  }

  ngOnDestroy() {}
}
